import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiUrl } from "../../config/api";
import ConfirmDialog from "../globalcontrols/confirmdialog/ConfirmDialog";
import AnimatedTabPanel from "./AnimatedTabPanel";
import "./tableeCarousel.css";

const emptyOption = {
  name: "",
  description: "",
  deliveryDays: "",
  price: "",
  freeShippingEnabled: false,
  freeShippingMinimum: "",
  availableCommunes: [],
  requiresDeliveryDate: false,
  deliveryMinDaysAhead: "2",
  sortOrder: "",
  isActive: true,
};

const TABLEE_DELIVERY_COMMUNES = [
  "Providencia",
  "Vitacura",
  "Ñuñoa",
  "Colina",
  "La Reina",
  "Las Condes",
  "Santiago",
  "Lo Barnechea",
];

function toLocalIsoDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseLocalIsoDate(value) {
  const [year, month, day] = String(value || "").split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function formatDateOnly(value) {
  const date = parseLocalIsoDate(value);
  if (!date) return value || "";
  return new Intl.DateTimeFormat("es-CL", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

function getSessionHeaders() {
  const session = JSON.parse(localStorage.getItem("sessionData") || "{}");
  return {
    Authorization: session.sessionKey || "",
    Username: session.username || "",
    "Content-Type": "application/json",
  };
}

function formatPrice(value) {
  return new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function TableeDelivery({ apiPrefix = "/tablee", storeName = "Tablee", storeLogo = "/images/tablee_logo.png", storeUrl = "www.tablee.cl" } = {}) {
  const [options, setOptions] = useState([]);
  const [form, setForm] = useState(emptyOption);
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [message, setMessage] = useState("");
  const [modalTab, setModalTab] = useState("data");
  const [isAddingCommune, setIsAddingCommune] = useState(false);
  const [newCommune, setNewCommune] = useState("");
  const [lockedDays, setLockedDays] = useState([]);
  const [selectedLockedDayDates, setSelectedLockedDayDates] = useState([]);
  const [lockedCalendarMonth, setLockedCalendarMonth] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const [showLockedDaysModal, setShowLockedDaysModal] = useState(false);
  const [savingLockedDays, setSavingLockedDays] = useState(false);

  const modalCommunes = Array.from(new Set([
    ...TABLEE_DELIVERY_COMMUNES,
    ...(Array.isArray(form.availableCommunes) ? form.availableCommunes : []),
  ])).sort((a, b) => a.localeCompare(b, "es"));

  const loadOptions = useCallback(async () => {
    const response = await fetch(apiUrl(`${apiPrefix}/delivery-options`), {
      headers: getSessionHeaders(),
    });
    const data = await response.json();
    if (data.success) {
      setOptions(data.options || []);
    } else {
      setMessage(data.message || "No se pudieron cargar los despachos.");
    }
  }, [apiPrefix]);

  const loadLockedDays = useCallback(async () => {
    const response = await fetch(apiUrl(`${apiPrefix}/delivery-locked-days`), {
      headers: getSessionHeaders(),
    });
    const data = await response.json();
    if (data.success) {
      setLockedDays(data.lockedDays || []);
    } else {
      setMessage(data.message || "No se pudieron cargar los días bloqueados.");
    }
  }, [apiPrefix]);

  useEffect(() => {
    loadOptions();
    loadLockedDays();
  }, [loadOptions, loadLockedDays]);

  useEffect(() => {
    if (!showModal) return undefined;
    function handleEscape(event) {
      if (event.key === "Escape") closeModal();
    }
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [showModal]);

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  function openCreateModal() {
    setEditingId(null);
    setForm({
      ...emptyOption,
      sortOrder: String(options.length + 1),
    });
    setMessage("");
    setModalTab("data");
    setIsAddingCommune(false);
    setNewCommune("");
    setShowModal(true);
  }

  function openLockedDaysModal() {
    setMessage("");
    setSelectedLockedDayDates([]);
    const today = new Date();
    setLockedCalendarMonth(new Date(today.getFullYear(), today.getMonth(), 1));
    loadLockedDays();
    setShowLockedDaysModal(true);
  }

  function closeLockedDaysModal() {
    setShowLockedDaysModal(false);
    setSelectedLockedDayDates([]);
  }

  const lockedDaySet = useMemo(
    () => new Set(lockedDays.map((day) => day.date).filter(Boolean)),
    [lockedDays]
  );

  const selectedLockedDaySet = useMemo(
    () => new Set(selectedLockedDayDates),
    [selectedLockedDayDates]
  );

  const selectedLockedDayMode = useMemo(() => {
    if (!selectedLockedDayDates.length) return "none";
    return lockedDaySet.has(selectedLockedDayDates[0]) ? "unlock" : "lock";
  }, [lockedDaySet, selectedLockedDayDates]);

  const lockedCalendarMonthLabel = useMemo(
    () => new Intl.DateTimeFormat("es-CL", { month: "long", year: "numeric" }).format(lockedCalendarMonth),
    [lockedCalendarMonth]
  );

  const lockedCalendarDays = useMemo(() => {
    const firstDay = new Date(lockedCalendarMonth.getFullYear(), lockedCalendarMonth.getMonth(), 1);
    const gridStart = new Date(firstDay);
    gridStart.setDate(firstDay.getDate() - firstDay.getDay());
    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(gridStart);
      date.setDate(gridStart.getDate() + index);
      const iso = toLocalIsoDate(date);
      return {
        iso,
        label: date.getDate(),
        isCurrentMonth: date.getMonth() === lockedCalendarMonth.getMonth(),
        isBlocked: lockedDaySet.has(iso),
        isSelected: selectedLockedDaySet.has(iso),
      };
    });
  }, [lockedCalendarMonth, lockedDaySet, selectedLockedDaySet]);

  function moveLockedCalendarMonth(direction) {
    setLockedCalendarMonth((current) => new Date(current.getFullYear(), current.getMonth() + direction, 1));
  }

  function toggleLockedDaySelection(date) {
    const isBlocked = lockedDaySet.has(date);
    setSelectedLockedDayDates((current) => {
      if (current.includes(date)) {
        return current.filter((entry) => entry !== date);
      }
      const currentMode = current.length && lockedDaySet.has(current[0]) ? "unlock" : "lock";
      const nextMode = isBlocked ? "unlock" : "lock";
      if (current.length && currentMode !== nextMode) {
        return [date];
      }
      return [...current, date].sort();
    });
  }

  async function toggleSelectedLockedDays() {
    setMessage("");
    if (savingLockedDays) return;
    if (!selectedLockedDayDates.length) {
      setMessage("Selecciona uno o más días para actualizar.");
      return;
    }
    setSavingLockedDays(true);
    try {
      for (const date of selectedLockedDayDates) {
        const isBlocked = lockedDaySet.has(date);
        const response = await fetch(
          apiUrl(isBlocked ? `${apiPrefix}/delivery-locked-days/${date}` : `${apiPrefix}/delivery-locked-days`),
          {
            method: isBlocked ? "DELETE" : "POST",
            headers: getSessionHeaders(),
            body: isBlocked ? undefined : JSON.stringify({ date }),
          }
        );
        const data = await response.json();
        if (!data.success) {
          setMessage(data.message || `No se pudo actualizar ${date}.`);
          await loadLockedDays();
          return;
        }
      }
      setSelectedLockedDayDates([]);
      await loadLockedDays();
    } finally {
      setSavingLockedDays(false);
    }
  }

  function openEditModal(option) {
    setEditingId(option.id);
    setForm({
      name: option.name || "",
      description: option.description || "",
      deliveryDays: option.deliveryDays || "",
      price: String(option.price ?? ""),
      freeShippingEnabled: option.freeShippingEnabled === true,
      freeShippingMinimum: String(option.freeShippingMinimum ?? ""),
      availableCommunes: Array.isArray(option.availableCommunes) ? option.availableCommunes : [],
      requiresDeliveryDate: option.requiresDeliveryDate === true,
      deliveryMinDaysAhead: String(option.deliveryMinDaysAhead ?? 2),
      sortOrder: String(option.sortOrder ?? ""),
      isActive: option.isActive !== false,
    });
    setMessage("");
    setModalTab("data");
    setIsAddingCommune(false);
    setNewCommune("");
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setEditingId(null);
    setForm(emptyOption);
    setModalTab("data");
    setIsAddingCommune(false);
    setNewCommune("");
  }

  async function saveOption(event) {
    event.preventDefault();
    setMessage("");

    const payload = {
      ...form,
      price: Number(form.price || 0),
      freeShippingMinimum: Number(form.freeShippingMinimum || 0),
      deliveryMinDaysAhead: Number(form.deliveryMinDaysAhead || 0),
      sortOrder: Number(form.sortOrder || 1),
    };
    const response = await fetch(apiUrl(editingId ? `${apiPrefix}/delivery-options/${editingId}` : `${apiPrefix}/delivery-options`), {
      method: editingId ? "PATCH" : "POST",
      headers: getSessionHeaders(),
      body: JSON.stringify(payload),
    });
    const data = await response.json();

    if (!data.success) {
      setMessage(data.message || "No se pudo guardar el despacho.");
      return;
    }

    closeModal();
    await loadOptions();
  }

  function normalizeCommune(value) {
    return String(value || "").trim().replace(/\s+/g, " ");
  }

  function isCommuneEnabled(commune) {
    const communes = Array.isArray(form.availableCommunes) ? form.availableCommunes : [];
    return communes.includes(commune);
  }

  function toggleCommune(commune) {
    setForm((current) => {
      const communes = Array.isArray(current.availableCommunes) ? current.availableCommunes : [];
      const exists = communes.includes(commune);
      return {
        ...current,
        availableCommunes: exists
          ? communes.filter((entry) => entry !== commune)
          : [...communes, commune],
      };
    });
  }

  function addCommune() {
    const commune = normalizeCommune(newCommune);
    if (!commune) return;

    setForm((current) => {
      const communes = Array.isArray(current.availableCommunes) ? current.availableCommunes : [];
      const exists = communes.some((entry) => entry.toLowerCase() === commune.toLowerCase());
      return {
        ...current,
        availableCommunes: exists ? communes : [...communes, commune],
      };
    });
    setNewCommune("");
    setIsAddingCommune(false);
  }

  function deleteOption(option) {
    setConfirmDialog({
      title: "Eliminar despacho",
      message: `Eliminar "${option.name}" de las opciones de despacho ${storeName}?`,
      confirmLabel: "Eliminar",
      onConfirm: () => confirmDeleteOption(option),
    });
  }

  async function confirmDeleteOption(option) {
    setConfirmDialog(null);
    setMessage("");
    const response = await fetch(apiUrl(`${apiPrefix}/delivery-options/${option.id}`), {
      method: "DELETE",
      headers: getSessionHeaders(),
    });
    const data = await response.json();

    if (!data.success) {
      setMessage(data.message || "No se pudo eliminar el despacho.");
      return;
    }

    await loadOptions();
  }

  async function toggleOptionActive(option) {
    setOptions((current) =>
      current.map((entry) => entry.id === option.id ? { ...entry, isActive: !option.isActive } : entry)
    );

    const response = await fetch(apiUrl(`${apiPrefix}/delivery-options/${option.id}/active`), {
      method: "PATCH",
      headers: getSessionHeaders(),
      body: JSON.stringify({ isActive: !option.isActive }),
    });
    const data = await response.json();

    if (!data.success) {
      setMessage(data.message || "No se pudo actualizar el estado.");
      await loadOptions();
    }
  }

  return (
    <div className="tablee-carousel tablee-delivery">
      <header className="tablee-carousel__header">
        <img src={storeLogo} alt={storeName} />
        <div>
          <span>{storeName}</span>
          <h1>Despachos</h1>
          <p>Administra los metodos de entrega, dias estimados y precios usados en el checkout de {storeUrl}.</p>
        </div>
        <div className="tablee-delivery__header-actions">
          <button type="button" onClick={openCreateModal}>Agregar despacho</button>
          <button type="button" className="tablee-delivery-locks__trigger" onClick={openLockedDaysModal}>Bloquear días</button>
        </div>
      </header>

      <section className="tablee-carousel__display">
        <div className="tablee-carousel__display-head">
          <div>
            <span>Checkout</span>
            <h2>Opciones configuradas</h2>
          </div>
          <strong>{options.length} opciones</strong>
        </div>
        <div className="tablee-carousel__table-header" aria-hidden="true">
          <span>Opcion</span>
          <span>Dias</span>
          <span>Precio</span>
          <span>Estado</span>
          <span>Acciones</span>
        </div>

        <div className="tablee-carousel__items">
          {options.length === 0 ? (
            <p className="tablee-carousel__empty">No hay opciones de despacho.</p>
          ) : null}
          {options.map((option) => (
            <article key={option.id} className={!option.isActive ? "is-hidden" : ""}>
              <div className="tablee-delivery__option-cell">
                <span>Orden {option.sortOrder}</span>
                <h2>{option.name}</h2>
                <p>{option.description || "Sin descripcion."}</p>
                <small>
                  {option.availableCommunes?.length
                    ? `${option.availableCommunes.length} comunas disponibles`
                    : "Todas las comunas (retiro o coordinado)"}
                </small>
                <small className="tablee-delivery__date-mode">
                  {option.requiresDeliveryDate ? `Solicita fecha · ${option.deliveryMinDaysAhead ?? 2} días mínimos` : "No solicita fecha"}
                </small>
                {option.freeShippingEnabled && Number(option.freeShippingMinimum || 0) > 0 ? (
                  <small className="tablee-delivery__date-mode">
                    Gratis desde {formatPrice(option.freeShippingMinimum)}
                  </small>
                ) : null}
              </div>
              <strong className="tablee-delivery__days">{option.deliveryDays}</strong>
              <strong>{formatPrice(option.price)}</strong>
              <label className="tablee-carousel__switch">
                <input type="checkbox" checked={option.isActive} onChange={() => toggleOptionActive(option)} />
                <span />
                <strong>{option.isActive ? "Activo" : "Oculto"}</strong>
              </label>
              <div className="tablee-delivery__row-actions">
                <button type="button" className="tablee-carousel__secondary tablee-delivery__icon-button" onClick={() => openEditModal(option)} aria-label={`Editar ${option.name}`}>
                  <span className="material-symbols-outlined">edit</span>
                </button>
                <button type="button" className="tablee-carousel__danger tablee-delivery__icon-button" onClick={() => deleteOption(option)} aria-label={`Eliminar ${option.name}`}>
                  <span className="material-symbols-outlined">delete</span>
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>

      {showModal ? (
        <div className="tablee-carousel__modal-backdrop" role="presentation" onMouseDown={closeModal}>
          <form
            className="tablee-carousel__modal"
            onSubmit={saveOption}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="tablee-carousel__modal-head">
              <div>
                <span>{storeName}</span>
                <h2>{editingId ? "Editar despacho" : "Agregar despacho"}</h2>
              </div>
              <button type="button" className="tablee-carousel__icon-button" onClick={closeModal} aria-label="Cerrar">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="tablee-delivery__tabs" role="tablist" aria-label="Secciones del despacho">
              <button type="button" className={modalTab === "data" ? "is-active" : ""} onClick={() => setModalTab("data")}>Datos</button>
              <button type="button" className={modalTab === "communes" ? "is-active" : ""} onClick={() => setModalTab("communes")}>Comunas</button>
            </div>

            <AnimatedTabPanel tabKey={modalTab}>
              {modalTab === "data" ? (
                <div className="tablee-carousel__form-grid">
                  <label>
                    Nombre
                    <input value={form.name} onChange={(event) => updateField("name", event.target.value)} />
                  </label>
                  <label>
                    Dias de entrega
                    <input value={form.deliveryDays} onChange={(event) => updateField("deliveryDays", event.target.value)} />
                  </label>
                  <label>
                    Precio
                    <input type="number" min="0" step="1" value={form.price} onChange={(event) => updateField("price", event.target.value)} />
                  </label>
                  <label>
                    Orden
                    <input type="number" min="1" step="1" value={form.sortOrder} onChange={(event) => updateField("sortOrder", event.target.value)} />
                  </label>
                  <label className="tablee-carousel__wide-field">
                    Descripcion
                    <textarea value={form.description} onChange={(event) => updateField("description", event.target.value)} />
                  </label>
                  <label className="tablee-carousel__wide-field tablee-carousel__switch tablee-delivery__date-toggle">
                    <input
                      type="checkbox"
                      checked={form.freeShippingEnabled}
                      onChange={(event) => updateField("freeShippingEnabled", event.target.checked)}
                    />
                    <span aria-hidden="true" />
                    <strong>
                      Despacho gratis sobre un monto mínimo
                      <small>El monto se calcula después de aplicar descuentos promocionales.</small>
                    </strong>
                  </label>
                  {form.freeShippingEnabled ? (
                    <label>
                      Monto mínimo para despacho gratis
                      <input
                        type="number"
                        min="1"
                        step="1"
                        required
                        value={form.freeShippingMinimum}
                        onChange={(event) => updateField("freeShippingMinimum", event.target.value)}
                      />
                    </label>
                  ) : null}
                  <label className="tablee-carousel__wide-field tablee-carousel__switch tablee-delivery__date-toggle">
                    <input
                      type="checkbox"
                      checked={form.requiresDeliveryDate}
                      onChange={(event) => updateField("requiresDeliveryDate", event.target.checked)}
                    />
                    <span aria-hidden="true" />
                    <strong>
                      Solicitar fecha de despacho en checkout
                      <small>Al elegir esta opción en {storeName}, el cliente deberá seleccionar un día disponible.</small>
                    </strong>
                  </label>
                  {form.requiresDeliveryDate ? (
                    <label>
                      Días mínimos de anticipación
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={form.deliveryMinDaysAhead}
                        onChange={(event) => updateField("deliveryMinDaysAhead", event.target.value)}
                      />
                    </label>
                  ) : null}
                </div>
              ) : (
                <section className="tablee-delivery__communes-panel">
                  <div className="tablee-delivery__communes-head">
                    <div>
                      <span>Disponibilidad</span>
                      <strong>Comunas disponibles</strong>
                      <small>Sin comunas, la opción se ofrece a todos sin revisar la dirección (úsalo para retiro en tienda).</small>
                    </div>
                    <div>
                      <button type="button" className="tablee-carousel__secondary" onClick={() => updateField("availableCommunes", modalCommunes)}>
                        Todas
                      </button>
                      <button type="button" className="tablee-delivery__icon-button" onClick={() => setIsAddingCommune(true)} aria-label="Agregar comuna">
                        <span className="material-symbols-outlined">add</span>
                      </button>
                    </div>
                  </div>
                  <div className="tablee-delivery__communes-table">
                    <div className="tablee-delivery__communes-table-head" aria-hidden="true">
                      <span>Comuna</span>
                      <span>Disponible</span>
                    </div>
                    <div className="tablee-delivery__communes-rows">
                      {isAddingCommune ? (
                        <div className="tablee-delivery__commune-row is-editing">
                          <input
                            value={newCommune}
                            onChange={(event) => setNewCommune(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                event.preventDefault();
                                addCommune();
                              }
                            }}
                            placeholder="Nueva comuna"
                            autoFocus
                          />
                          <div>
                            <button type="button" className="tablee-carousel__secondary tablee-delivery__icon-button" onClick={addCommune} aria-label="Guardar comuna">
                              <span className="material-symbols-outlined">check</span>
                            </button>
                            <button type="button" className="tablee-carousel__secondary tablee-delivery__icon-button" onClick={() => { setIsAddingCommune(false); setNewCommune(""); }} aria-label="Cancelar comuna">
                              <span className="material-symbols-outlined">close</span>
                            </button>
                          </div>
                        </div>
                      ) : null}
                      {modalCommunes.map((commune) => (
                        <div className="tablee-delivery__commune-row" key={commune}>
                          <strong>{commune}</strong>
                          <label className="tablee-carousel__switch">
                            <input type="checkbox" checked={isCommuneEnabled(commune)} onChange={() => toggleCommune(commune)} />
                            <span />
                            <strong>{isCommuneEnabled(commune) ? "Activa" : "Inactiva"}</strong>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                  <p>Activa solo las comunas donde esta opcion de despacho debe estar disponible.</p>
                </section>
              )}
            </AnimatedTabPanel>

            <div className="tablee-carousel__modal-actions tablee-delivery__modal-actions">
              <div className="tablee-delivery__footer-left">
                <label className="tablee-home-admin__publish-toggle tablee-delivery__publish-toggle">
                  <span>Publicado</span>
                  <input type="checkbox" checked={form.isActive} onChange={(event) => updateField("isActive", event.target.checked)} />
                  <i aria-hidden="true" />
                </label>
                {message ? <p>{message}</p> : null}
              </div>
              <div className="tablee-delivery__footer-buttons">
                <button type="button" className="tablee-carousel__secondary" onClick={closeModal}>Cancelar</button>
                <button type="submit">{editingId ? "Guardar cambios" : "Agregar despacho"}</button>
              </div>
            </div>
          </form>
        </div>
      ) : null}

      {showLockedDaysModal ? (
        <div className="tablee-delivery-locks__backdrop" role="presentation" onMouseDown={closeLockedDaysModal}>
          <section
            className="tablee-delivery-locks__modal"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="tablee-delivery-locks__modal-head">
              <div>
                <span>Despachos</span>
                <h2>Bloquear días</h2>
              </div>
              <button type="button" className="tablee-delivery-locks__close" onClick={closeLockedDaysModal} aria-label="Cerrar">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="tablee-delivery-locks__body">
              <div className="tablee-delivery-locks__calendar">
                <div className="tablee-delivery-locks__calendar-head">
                  <button type="button" onClick={() => moveLockedCalendarMonth(-1)} aria-label="Mes anterior">
                    <span className="material-symbols-outlined">chevron_left</span>
                  </button>
                  <strong>{lockedCalendarMonthLabel}</strong>
                  <button type="button" onClick={() => moveLockedCalendarMonth(1)} aria-label="Mes siguiente">
                    <span className="material-symbols-outlined">chevron_right</span>
                  </button>
                </div>
                <div className="tablee-delivery-locks__weekdays" aria-hidden="true">
                  {["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"].map((day) => (
                    <span key={day}>{day}</span>
                  ))}
                </div>
                <div className="tablee-delivery-locks__days">
                  {lockedCalendarDays.map((day) => (
                    <button
                      key={day.iso}
                      type="button"
                      className={[
                        day.isCurrentMonth ? "" : "is-muted",
                        day.isSelected ? "is-selected" : "",
                        day.isBlocked ? "is-blocked" : "",
                      ].filter(Boolean).join(" ")}
                    onClick={() => toggleLockedDaySelection(day.iso)}
                    aria-pressed={day.isSelected}
                    aria-label={day.isBlocked ? `${formatDateOnly(day.iso)} bloqueado, seleccionar para desbloquear` : formatDateOnly(day.iso)}
                    >
                      <span>{day.label}</span>
                    </button>
                  ))}
                </div>
                <div className="tablee-delivery-locks__legend">
                  <span><i className="is-selected" /> Seleccionado</span>
                  <span><i className="is-blocked" /> Bloqueado</span>
                </div>
              </div>

              <aside className="tablee-delivery-locks__sidebar" aria-label="Bloquear días seleccionados">
                <div className="tablee-delivery-locks__sidebar-head">
                  <span>Selección</span>
                  <strong>{selectedLockedDayDates.length}</strong>
                  <small>
                    {selectedLockedDayMode === "lock"
                      ? `${selectedLockedDayDates.length} por bloquear`
                      : selectedLockedDayMode === "unlock"
                        ? `${selectedLockedDayDates.length} por desbloquear`
                        : "elige días bloqueados o disponibles"}
                  </small>
                </div>

                <div className="tablee-delivery-locks__sidebar-action">
                  <div>
                    <span>{selectedLockedDayMode === "unlock" ? "Desbloquear días" : "Bloquear días"}</span>
                    <small>
                      {savingLockedDays
                        ? "Guardando..."
                        : selectedLockedDayMode === "unlock"
                          ? "Desactiva para liberar la selección"
                          : "Activa para bloquear la selección"}
                    </small>
                  </div>
                  <button
                    type="button"
                    className={[
                      "tablee-delivery-locks__block-switch",
                      selectedLockedDayMode === "unlock" || savingLockedDays ? "is-on" : "",
                      savingLockedDays ? "is-saving" : "",
                    ].filter(Boolean).join(" ")}
                    role="switch"
                    aria-checked={selectedLockedDayMode === "unlock" || savingLockedDays}
                    aria-label={selectedLockedDayMode === "unlock" ? "Desbloquear días seleccionados" : "Bloquear días seleccionados"}
                    disabled={!selectedLockedDayDates.length || savingLockedDays}
                    onClick={toggleSelectedLockedDays}
                  >
                    <i aria-hidden="true" />
                  </button>
                </div>
              </aside>
            </div>

            <div className="tablee-delivery-locks__footer">
              <div>{message ? <p>{message}</p> : null}</div>
              <button type="button" className="tablee-delivery-locks__done" onClick={closeLockedDaysModal}>Cerrar</button>
            </div>
          </section>
        </div>
      ) : null}

      <ConfirmDialog
        open={Boolean(confirmDialog)}
        title={confirmDialog?.title}
        message={confirmDialog?.message}
        confirmLabel={confirmDialog?.confirmLabel}
        onConfirm={confirmDialog?.onConfirm}
        onCancel={() => setConfirmDialog(null)}
      />
    </div>
  );
}

export default TableeDelivery;
